import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import CreatorNavbar from '../dashboard/CreatorNavbar';
import api from '../../api';

const CreatorProfile = () => {
  const { id } = useParams(); // for viewing any creator's profile
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) return;
    
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/creators/${id}/profile`);
        setProfile(response.data);
      } catch (error) {
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Profile Not Found</h2>
          <p className="text-gray-600 mb-4">{error || 'The requested profile could not be found.'}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // SEO optimization
  const profileName = profile.user?.name || 'creator';
  const profileBio = profile.bio || `Connect with ${profileName} on CollabOrb`;
  const pageTitle = `${profileName} - Creator Profile | CollabOrb`;
  const pageDescription = `Explore ${profileName}'s Creator profile on CollabOrb. ${profileBio.substring(0, 150)}`;

  // Check if this is the user's own profile
  // Use profile.userId from API response for accurate comparison
  // Convert both to numbers for comparison since userId might be stored as string in localStorage
  const isOwnProfile = user && profile.userId &&
    parseInt(user.userId) === parseInt(profile.userId);

  // Debug logging (remove after testing)
  console.log('=== Public Profile Debug ===');
  console.log('user:', user);
  console.log('user.userId (original):', user?.userId);
  console.log('user.userId (parsed):', user?.userId ? parseInt(user.userId) : null);
  console.log('profile.userId:', profile.userId);
  console.log('isOwnProfile:', isOwnProfile);
  console.log('=========================');

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={`${profileName}, Creator profile, social media Creator, brand partnerships, content Creator, CollabOrb`} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://collaborb.com/creator/${id}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={`https://collaborb.com/creator/${id}`} />
        {profile.profilePictureUrl && (
          <meta property="og:image" content={profile.profilePictureUrl} />
        )}
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {profile.profilePictureUrl && (
          <meta name="twitter:image" content={profile.profilePictureUrl} />
        )}
        
        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            "name": profileName,
            "description": profileBio,
            "url": `https://collaborb.com/creator/${id}`,
            "image": profile.profilePictureUrl,
            "sameAs": [
              profile.instagramHandle && `https://instagram.com/${profile.instagramHandle}`,
              profile.twitterHandle && `https://twitter.com/${profile.twitterHandle}`,
              profile.facebookHandle && `https://facebook.com/${profile.facebookHandle}`,
              profile.website
            ].filter(Boolean),
            "jobTitle": "Content Creator",
            "worksFor": {
              "@type": "Organization",
              "name": "CollabOrb"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Navigation Bar */}
        {isOwnProfile ? (
          <CreatorNavbar />
        ) : (
          <div className="bg-white shadow-sm border-b">
            <div className="max-w-6xl mx-auto px-4 py-4">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Back</span>
              </button>
            </div>
          </div>
        )}

        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Profile Header */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 h-32"></div>
            <div className="px-8 pb-8">
              <div className="flex flex-col md:flex-row md:items-end md:space-x-8 -mt-16">
                <div className="flex-shrink-0 mb-4 md:mb-0">
                  {profile.profilePictureUrl ? (
                    <img
                      src={profile.profilePictureUrl}
                      alt={profileName}
                      className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg bg-gray-100 flex items-center justify-center">
                      <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                  )}
                </div>
                
                <div className="flex-1 mt-4 md:mt-0">
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{profileName}</h1>
                  <div className="flex items-center space-x-4 mb-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      Content Creator
                    </span>
                    {profile.location && (
                      <span className="flex items-center text-gray-600">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {profile.location}
                      </span>
                    )}
                  </div>
                  {profile.bio && (
                    <p className="text-gray-700 text-lg leading-relaxed max-w-3xl">{profile.bio}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Statistics Cards */}
          {(profile.followers || profile.engagement || profile.contentCategories) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {profile.followers && (
                <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">{profile.followers.toLocaleString()}</div>
                  <div className="text-gray-600">Total Followers</div>
                </div>
              )}
              {profile.engagement && (
                <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">{profile.engagement}%</div>
                  <div className="text-gray-600">Engagement Rate</div>
                </div>
              )}
              {profile.contentCategories && (
                <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">{profile.contentCategories.length}</div>
                  <div className="text-gray-600">Content Categories</div>
                </div>
              )}
            </div>
          )}

          {/* Content Categories */}
          {profile.contentCategories && profile.contentCategories.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Content Categories</h2>
              <div className="flex flex-wrap gap-3">
                {profile.contentCategories.map((category, index) => (
                  <span
                    key={`category-${category}-${index}`}
                    className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                  >
                    {category}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Social Media Links */}
          {(profile.instagramUrl || profile.twitterUrl || profile.youtubeUrl || profile.tiktokUrl || profile.website) && (
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Connect With Me</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {profile.instagramUrl && (
                  <a
                    href={profile.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-pink-50 hover:border-pink-300 transition-colors"
                  >
                    <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-purple-600 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.618 5.367 11.986 11.988 11.986s11.987-5.368 11.987-11.986C24.004 5.367 18.635.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.864 3.68 13.664 3.68 12.315c0-1.297.518-2.498 1.446-3.323.875-.807 2.026-1.297 3.323-1.297s2.447.49 3.322 1.297c.928.825 1.447 2.026 1.447 3.323 0 1.349-.519 2.549-1.447 3.376-.875.807-2.025 1.297-3.322 1.297zm7.138 0c-1.297 0-2.447-.49-3.322-1.297-.928-.827-1.447-2.027-1.447-3.376 0-1.297.519-2.498 1.447-3.323.875-.807 2.025-1.297 3.322-1.297s2.448.49 3.323 1.297c.928.825 1.446 2.026 1.446 3.323 0 1.349-.518 2.549-1.446 3.376-.875.807-2.026 1.297-3.323 1.297z"/>
                      </svg>
                    </div>
                    <span className="font-medium text-gray-900">Instagram</span>
                  </a>
                )}
                {profile.twitterUrl && (
                  <a
                    href={profile.twitterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors"
                  >
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                      </svg>
                    </div>
                    <span className="font-medium text-gray-900">Twitter</span>
                  </a>
                )}
                {profile.youtubeUrl && (
                  <a
                    href={profile.youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-red-50 hover:border-red-300 transition-colors"
                  >
                    <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                    </div>
                    <span className="font-medium text-gray-900">YouTube</span>
                  </a>
                )}
                {profile.tiktokUrl && (
                  <a
                    href={profile.tiktokUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors"
                  >
                    <div className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19.589 6.686a4.793 4.793 0 0 1-3.77-4.245V2h-3.445v13.672a2.896 2.896 0 0 1-5.201 1.743l-.002-.001.002.001a2.895 2.895 0 0 1 3.183-4.51v-3.5a6.329 6.329 0 0 0-5.394 10.692 6.33 6.33 0 0 0 10.857-4.424V8.687a8.182 8.182 0 0 0 4.773 1.526V6.79a4.831 4.831 0 0 1-1.003-.104z"/>
                      </svg>
                    </div>
                    <span className="font-medium text-gray-900">TikTok</span>
                  </a>
                )}
                {profile.website && (
                  <a
                    href={profile.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-green-50 hover:border-green-300 transition-colors"
                  >
                    <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9m0 9c-5 0-9-4-9-9s4-9 9-9" />
                      </svg>
                    </div>
                    <span className="font-medium text-gray-900">Website</span>
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Portfolio Section */}
          {profile.portfolioItems && profile.portfolioItems.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Portfolio</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {profile.portfolioItems.map((item, index) => (
                  <div key={`portfolio-${item.title || index}-${index}`} className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                    {item.imageUrl && (
                      <img
                        src={item.imageUrl}
                        alt={item.title || `Portfolio item ${index + 1}`}
                        className="w-full h-48 object-cover"
                      />
                    )}
                    <div className="p-4">
                      {item.title && (
                        <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                      )}
                      {item.description && (
                        <p className="text-gray-600 text-sm">{item.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contact Information */}
          {(profile.user?.email || profile.contactNumber) && (
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h2>
              <div className="space-y-4">
                {profile.user?.email && (
                  <div className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span className="text-gray-700">{profile.user.email}</span>
                  </div>
                )}
                {profile.contactNumber && (
                  <div className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span className="text-gray-700">{profile.contactNumber}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CreatorProfile;
